/**
 * Recovery for a tab that outlived a deploy.
 *
 * A page rendered from an older build can lazily request a JS chunk the new
 * build no longer serves. The import rejects with a `ChunkLoadError` and the
 * route dies, which is what the "update ready — refresh" prompt used to guard
 * against by asking the visitor to reload. One hard reload does the same thing
 * without depending on the visitor noticing a toast.
 */

const RELOAD_KEY = "pf:chunk-reload";

/**
 * Whether a thrown value is a failed chunk load. Turbopack sets
 * `name = "ChunkLoadError"`; the message patterns cover webpack builds and any
 * wrapper that loses the name.
 */
export function isChunkLoadError(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const { name, message } = value as { name?: unknown; message?: unknown };
  if (name === "ChunkLoadError") return true;
  return (
    typeof message === "string" &&
    /Failed to load chunk|Loading chunk \S+ failed|error loading dynamically imported module/i.test(
      message,
    )
  );
}

type ReloadStorage = Pick<Storage, "getItem" | "setItem">;

/**
 * Claims the one reload this tab is allowed for a chunk error, returning
 * whether the caller should reload.
 *
 * The budget of one is the point: if the chunk is genuinely missing — a broken
 * deploy rather than a stale tab — an unguarded reload would spin the tab
 * forever. A second stale-build event in the same tab falls through to the
 * error boundary's retry, exactly as it did before.
 */
export function shouldReloadForChunkError(
  value: unknown,
  storage: ReloadStorage | undefined,
): boolean {
  if (!isChunkLoadError(value)) return false;
  // Private windows and blocked site data make storage throw on access, and a
  // reload with no way to record it is the loop this guard exists to prevent.
  try {
    if (!storage || storage.getItem(RELOAD_KEY)) return false;
    storage.setItem(RELOAD_KEY, "1");
    return true;
  } catch {
    return false;
  }
}
